import { useState } from "react";
import { Navigate } from "react-router-dom";
import { LogIn } from "lucide-react";
import { Logo, Wordmark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { mydataError } from "@/api";

export default function Login() {
  const { t } = useLang();
  const { user, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await login(username, password); } catch (err) { setError(mydataError(err, t)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex bg-slate-950 text-slate-100 font-body" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between w-[46%] p-14 bg-blue-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #60a5fa 0, transparent 40%), radial-gradient(circle at 80% 80%, #1e3a8a 0, transparent 45%)" }} />
        <div className="relative">
          <Logo size={48} dark subtitle={t("app_subtitle")} />
        </div>
        <div className="relative space-y-4">
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl leading-tight tracking-tight">{t("login_hero")}</h1>
          <p className="text-blue-200 text-base max-w-md">{t("login_hero_sub")}</p>
        </div>
        <div className="relative text-xs text-blue-300 font-mono">v0.1 · {new Date().getFullYear()}</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <Wordmark className="text-5xl lg:hidden block mb-6" />
            <h2 className="font-heading font-bold text-2xl tracking-tight">{t("login_title")}</h2>
            <p className="text-sm text-slate-400 mt-1">{t("login_sub")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400">{t("username")}</Label>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoFocus className="bg-slate-900 border-slate-700 font-mono" data-testid="login-username-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400">{t("password")}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" className="bg-slate-900 border-slate-700 font-mono" data-testid="login-password-input" />
          </div>
          {error && <div className="text-sm text-rose-300 bg-rose-950/50 border border-rose-900 rounded px-3 py-2" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={busy || !username || !password} className="w-full bg-blue-700 hover:bg-blue-600 h-11" data-testid="login-submit-button">
            <LogIn size={16} className="mr-2" />{t("login_button")}
          </Button>
        </form>
      </div>
    </div>
  );
}
